<template>
  <v-card v-if="rows.length" class="country-card">
    <v-card-title class="country-card__title">
      Länder-Übersicht
      <span class="country-card__hint">
        Versand-Land der Orders · Werbekosten nach Ausspielungs-Land · Gewinn = Umsatz − Bestellkosten − Werbekosten (ohne Fixkosten)
      </span>
    </v-card-title>
    <v-card-text>
      <div class="country-card__head">
        <span class="country-card__flag"></span>
        <span class="country-card__bar-track country-card__bar-track--head"></span>
        <span class="country-card__num country-card__num--wide">Umsatz</span>
        <span class="country-card__num">Orders</span>
        <span class="country-card__num country-card__num--wide">Bestellkosten</span>
        <span class="country-card__num country-card__num--wide">Werbekosten</span>
        <span class="country-card__num country-card__num--wide">Gewinn</span>
        <span class="country-card__num">Marge</span>
        <span class="country-card__num">ROAS</span>
      </div>
      <template v-for="row in rows">
        <div
          :key="row.country"
          class="country-card__row"
          :class="{ 'country-card__row--expandable': row.adTotal > 0 }"
          @click="row.adTotal > 0 && toggle(row.country)"
        >
          <span class="country-card__flag">
            {{ flag(row.country) }} {{ row.country }}
            <v-icon v-if="row.adTotal > 0" x-small class="country-card__chev">
              {{ expanded.includes(row.country) ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </span>
          <span class="country-card__bar-track">
            <span
              class="country-card__bar"
              :style="{ width: row.share + '%', background: barColor(row.share) }"
            />
          </span>
          <span class="country-card__num country-card__num--wide country-card__num--strong">{{ formatEuro(row.revenue) }}</span>
          <span class="country-card__num country-card__num--muted">{{ row.orders }}</span>
          <span class="country-card__num country-card__num--wide">{{ formatEuro(row.orderCosts) }}</span>
          <span class="country-card__num country-card__num--wide">{{ formatEuro(row.adTotal) }}</span>
          <span
            class="country-card__num country-card__num--wide country-card__num--strong"
            :style="{ color: profitColor(row) }"
          >{{ formatEuro(row.profit) }}</span>
          <span class="country-card__num" :style="{ color: profitColor(row) }">{{ row.margin }}</span>
          <span class="country-card__num country-card__num--muted">{{ row.roas }}</span>
        </div>
        <div
          v-if="expanded.includes(row.country)"
          :key="row.country + '_channels'"
          class="country-card__channels"
        >
          <span
            v-for="(spend, channel) in row.adByChannel"
            :key="channel"
            class="country-card__chip"
            :style="{ borderColor: channelColor(channel) }"
          >
            <span class="country-card__dot" :style="{ background: channelColor(channel) }" />
            {{ channelLabel(channel) }}: {{ formatEuro(spend) }}
          </span>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { getDecimalNumberString } from "@/utils";
import { statisticsColors } from "@/config";

export default {
  name: "CountryRevenueCard",
  props: {
    shops: { type: Array, default: () => [] },
  },
  data() {
    return { expanded: [] };
  },
  computed: {
    rows() {
      const byCountry = {};
      const entry = (cc) => {
        if (!byCountry[cc]) {
          byCountry[cc] = { revenue: 0, orders: 0, orderCosts: 0, adTotal: 0, adByChannel: {} };
        }
        return byCountry[cc];
      };
      for (const shop of this.shops) {
        for (const [cc, revenue] of Object.entries(shop.revenue?.byCountry || {})) {
          entry(cc).revenue += revenue || 0;
        }
        for (const [cc, orders] of Object.entries(shop.ordersByCountry || {})) {
          entry(cc).orders += orders || 0;
        }
        for (const [cc, costs] of Object.entries(shop.costsByCountry || shop.costs?.byCountry || {})) {
          entry(cc).orderCosts += costs || 0;
        }
        for (const [cc, ad] of Object.entries(shop.advertisement?.byCountry || {})) {
          const e = entry(cc);
          e.adTotal += ad?.total || 0;
          for (const [channel, spend] of Object.entries(ad || {})) {
            if (channel === "total" || !spend) continue;
            e.adByChannel[channel] = (e.adByChannel[channel] || 0) + spend;
          }
        }
      }
      const entries = Object.entries(byCountry)
        .map(([country, data]) => {
          const profit = data.revenue - data.orderCosts - data.adTotal;
          return {
            country,
            ...data,
            profit,
            margin: data.revenue > 0 ? ((profit / data.revenue) * 100).toFixed(0) + " %" : "–",
            roas: data.adTotal > 0 ? (data.revenue / data.adTotal).toFixed(2) : "–",
          };
        })
        .sort((a, b) => b.revenue - a.revenue);
      const max = entries[0]?.revenue || 1;
      return entries.map((entry) => ({
        ...entry,
        share: Math.max(2, Math.round((entry.revenue / max) * 100)),
      }));
    },
  },
  methods: {
    toggle(cc) {
      this.expanded = this.expanded.includes(cc)
        ? this.expanded.filter((c) => c !== cc)
        : [...this.expanded, cc];
    },
    flag(cc) {
      if (!/^[A-Z]{2}$/.test(cc)) return "🌐";
      return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
    },
    profitColor(row) {
      if (row.profit > 0) return "#1d9d55";
      if (row.profit < 0) return "#d23f3f";
      return "rgba(0,0,0,0.55)";
    },
    /** Sequentiell: eine Hue, mehr Umsatz = dunkler */
    barColor(share) {
      if (share > 66) return "#256abf";
      if (share > 33) return "#5598e7";
      return "#86b6ef";
    },
    channelColor(channel) {
      return statisticsColors[channel] || "#9e9e9e";
    },
    channelLabel(channel) {
      const labels = { facebook: "Facebook", google: "Google", tiktok: "TikTok", chatgpt: "ChatGPT" };
      return labels[channel] || channel;
    },
    formatEuro(value) {
      return `${getDecimalNumberString(value || 0, 2)} €`;
    },
  },
};
</script>

<style lang="scss" scoped>
.country-card {
  border-radius: 12px !important;
  height: 100%;

  &__title {
    font-size: 16px;
    font-weight: 700;
    padding-bottom: 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__hint {
    font-size: 11px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.4);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 6px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 0;
    border-radius: 6px;

    &--expandable {
      cursor: pointer;

      &:hover {
        background: #f6f5fa;
      }
    }
  }

  &__flag {
    width: 78px;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
    white-space: nowrap;
  }

  &__chev {
    color: rgba(0, 0, 0, 0.35) !important;
  }

  &__bar-track {
    flex: 2;
    min-width: 120px;
    background: #eef0f3;
    border-radius: 4px;
    height: 14px;
    overflow: hidden;
    display: block;

    &--head {
      background: transparent;
      height: auto;
    }
  }

  &__bar {
    display: block;
    height: 100%;
    border-radius: 4px 3px 3px 4px;
    transition: width 0.3s ease;
  }

  &__num {
    flex: 1;
    min-width: 52px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;

    &--wide {
      flex: 1.5;
      min-width: 84px;
    }

    &--strong {
      font-weight: 700;
    }

    &--muted {
      color: rgba(0, 0, 0, 0.45);
      font-size: 11.5px;
    }
  }

  &__channels {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 2px 0 8px 86px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 9px;
    border: 1px solid;
    border-radius: 999px;
    color: rgba(0, 0, 0, 0.7);
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
}
</style>
